if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Add event listener for insurance input to update total price
        this.setupInsuranceListener();
        
        // Handle URL parameters for media type selection
        this.handleURLParameters();
      }

      // Title-case free-text fields without clobbering intentional casing.
      // Each whitespace-delimited word that already contains an uppercase
      // letter is left untouched (preserves AC/DC, R.E.M., NWA, McCartney,
      // THE BEATLES, etc.). All-lowercase words get their first letter
      // capitalized, so "the beatles" / "The beatles" -> "The Beatles".
      formatTitleCase(value) {
        return value.replace(/\S+/g, (word) =>
          /[A-Z]/.test(word) ? word : word.replace(/[a-z]/, (c) => c.toUpperCase())
        );
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Get all radio inputs and text inputs from the entire document (not just form)
        const radioInputs = document.querySelectorAll('input[type="radio"]:checked');
        const textInputs = document.querySelectorAll('input[type="text"], textarea');
        const priceRange = document.getElementById('priceRange');
        const priceRangeSlider = document.getElementById('priceRangeSlider');

        // Generate submission ID before form submission 
        // Update hidden properties from radio inputs
        radioInputs.forEach(input => {
          const propertyName = input.getAttribute('name');
          if (propertyName && propertyName.startsWith('properties[')) {
            const propertyInput = this.form.querySelector(`input[name="${propertyName}"]`);
            if (propertyInput) {
              propertyInput.value = input.value;
            }
          }
        });

        // Update hidden properties from text inputs and textareas
        textInputs.forEach(input => {
          const propertyName = input.getAttribute('name');
          if (propertyName && propertyName.startsWith('properties[')) {
            const propertyInput = this.form.querySelector(`input[name="${propertyName}"]`);
            if (propertyInput && propertyInput !== input) { // Don't update if it's the same element
              propertyInput.value = this.formatTitleCase(input.value);
            }
          }
        });

        // Update price range
        if (priceRange && priceRangeSlider) {
          const priceRangeInput = this.form.querySelector('input[name="properties[price-range]"]');
          if (priceRangeInput) {
            priceRangeInput.value = `${priceRange.textContent}`;
          }
        }

        // Get and update total price amount
        const totalPriceElement = document.querySelector('.total-price-amount');
        if (totalPriceElement) {
          const totalPriceInput = this.form.querySelector('input[name="properties[total_amount]"]');
          if (totalPriceInput) {
            totalPriceInput.value = totalPriceElement.textContent;
          }
        }

        // Update custom note
        const customNote = document.querySelector('textarea[name="properties[custom-note]"]');
        if (customNote) {
          const customNoteInput = this.form.querySelector('input[name="properties[custom-note]"]');
          if (customNoteInput) {
            customNoteInput.value = this.formatTitleCase(customNote.value);
          }
        }

        // Update insurance field
        const insuranceInput = document.querySelector('#how-much-would-you-like-to-insure-your-item');
        if (insuranceInput) {
          const insuranceHiddenInput = this.form.querySelector(
            'input[name="properties[how-much-would-you-like-to-insure-your-item]"]'
          );
          if (insuranceHiddenInput) {
            insuranceHiddenInput.value = this.formatTitleCase(insuranceInput.value);
          }
        }

        // Handle keyhole mounts based on media type
        const selectedMediaType = document.querySelector('input[name="properties[media-type]"]:checked');
        if (selectedMediaType) {
          const mediaTypeValue = selectedMediaType.value;
          const mediaTypeDataType = selectedMediaType.getAttribute('data-type');
          
          // Check if this is a vinyl case media type
          if (mediaTypeValue.toLowerCase().includes('vinyl') || mediaTypeDataType === 'vinyl' || mediaTypeDataType === 'vinyl-case') {
            // Find keyhole mount checkboxes
            const keyholeCheckboxes = document.querySelectorAll('input[name="properties[keyhole-mounts][]"]:checked');
            const keyholeHiddenInput = this.form.querySelector('input[name="properties[keyhole-mounts]"]');
            
            if (keyholeHiddenInput) {
              // Set to "yes" if any checkbox is checked, "no" if none are checked
              keyholeHiddenInput.value = keyholeCheckboxes.length > 0 ? "yes" : "no";
            }
          } else {
            // For other media types, handle normally (collect checkbox values)
            const keyholeCheckboxes = document.querySelectorAll('input[name="properties[keyhole-mounts][]"]:checked');
            const keyholeHiddenInput = this.form.querySelector('input[name="properties[keyhole-mounts]"]');
            
            if (keyholeHiddenInput) {
              const values = Array.from(keyholeCheckboxes).map(cb => cb.value);
              keyholeHiddenInput.value = values.join(', ');
            }
          }
        }

        // --- Submission integrity guard ---------------------------------
        // A grading submission must carry a computed price and an AMG SKU.
        // When the same item is added again within ~1s (an impatient double
        // click, or while the media-type reset cascade has the page busy),
        // the price can read $0 and the SKU generator in buy-buttons.liquid
        // is skipped by its 1-second debounce — producing a $0, SKU-less
        // line (see order #22590). Ensure a SKU here, and block the add if
        // the price hasn't computed, rather than booking a broken line.
        const submissionIdInput = this.form.querySelector('input[name="properties[submission_id]"]');
        if (submissionIdInput && !submissionIdInput.value.trim()) {
          submissionIdInput.value = `AMG${Math.floor(Math.random() * 90000) + 10000}`;
        }

        const totalAmountInput = this.form.querySelector('input[name="properties[total_amount]"]');
        const computedTotal = parseFloat(String(totalAmountInput && totalAmountInput.value || '').replace(/[^0-9.]/g, ''));
        if (!(computedTotal > 0)) {
          // Legitimate $0s come from discount/comp codes at checkout, never
          // from a normal product-form submission — so a $0 here is the bug.
          this.handleErrorMessage('Still calculating your price — please try again in a moment.');
          if (window.priceUpdater && typeof window.priceUpdater.updateTotalPrice === 'function') {
            window.priceUpdater.updateTotalPrice();
          }
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
/**
 * 
 */
              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                      this.resetForm();
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
                this.resetForm();
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", event);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      resetForm() {
        // Reset the main form
        this.form.reset();
        this.getBasePrice();
        // Reset all checkboxes
        const checkboxes = document.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach(checkbox => {
          checkbox.checked = false;
        });
        
        const insuranceInput = document.querySelector('#how-much-would-you-like-to-insure-your-item');
        if (insuranceInput) {
          insuranceInput.value = '';
        }
        
        // Reset all text inputs and textareas
        const textInputs = document.querySelectorAll('input[type="text"], textarea');
        textInputs.forEach(input => {
          input.value = '';
        });
        
        // Reset price range display
        const priceRange = document.getElementById("priceRange");
        const priceRangeInput = this.form.querySelector('input[name="properties[price-range]"]');
        const priceRangeSlider = document.getElementById("priceRangeSlider");
        const priceSlider = document.querySelector(".price-range-slide");
        if (priceRange) priceRange.textContent = '0-499.99';
        if (priceRangeInput) priceRangeInput.value = 0;
        if (priceRangeSlider) priceRangeSlider.textContent = '$0';
        if (priceRangeSlider) priceRangeSlider.style.display = 'none';
        if (priceSlider) priceSlider.textContent = '$0';
        
        // Reset any range sliders
        const rangeInputs = document.querySelectorAll('input[type="range"]');
        rangeInputs.forEach(input => {
          input.value = input.min || 0;
        });
        
        // Reset select dropdowns
        const selectElements = document.querySelectorAll('select');
        selectElements.forEach(select => {
          select.selectedIndex = 0;
        });
        
        // First uncheck all radio buttons except the ones we want to keep as default
        const radioInputs = document.querySelectorAll('input[type="radio"]');
        radioInputs.forEach(input => {
          input.checked = false;
        });
        
        // Set specific default radio buttons
        const defaultSelections = [
          'input[name="properties[media-type]"][value="Vinyl"]',
          'input[name="properties[service-type]"][value="Grading (We authenticate, document, label and case with a grade)"]',
          'input[name="properties[expedited]"][value="None"]',
          'input[name="properties[authentication]"][value="None"]'
        ];
        
        defaultSelections.forEach(selector => {
          const radioButton = document.querySelector(selector);
          if (radioButton) {
            radioButton.checked = true;
          }
        });
        
        // Reset any custom UI states by triggering change events (but not on radio buttons)
        const allInputs = document.querySelectorAll('input:not([type="radio"]), select, textarea');
        allInputs.forEach(input => {
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        
        // Trigger change events for default radio buttons to update pricing
        defaultSelections.forEach(selector => {
          const radioButton = document.querySelector(selector);
          if (radioButton) {
            radioButton.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        
        // Calculate and set default total price after a short delay
        setTimeout(() => {
          const basePrice = this.getBasePrice();
          const totalPriceElement = document.querySelector('.total-price-amount');
          const priceRangeSlider = document.getElementById('priceRangeSlider');
          
          if (totalPriceElement) {
            let totalPrice = basePrice;
            
            // Add insurance fee if available
            if (priceRangeSlider) {
              const insuranceFeeText = priceRangeSlider.textContent;
              const insuranceFee = parseFloat(insuranceFeeText.replace(/[^0-9.-]+/g, '')) || 0;
              const insuranceFeeInput = document.getElementById("insuranceFeeInput");
              insuranceFeeInput.value = insuranceFee;
              totalPrice += insuranceFee;
            }
            
            if (totalPrice > 0) {
              const formattedPrice = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                trailingZeroDisplay: 'stripIfInteger',
              }).format(totalPrice);
              totalPriceElement.textContent = formattedPrice;
            }
          }
          
          if (window.priceUpdater && typeof window.priceUpdater.updateTotalPrice === 'function') {
            window.priceUpdater.updateTotalPrice();
          }
        }, 200);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }

      setupInsuranceListener() {
        const insuranceInput = document.querySelector('#how-much-would-you-like-to-insure-your-item');
        const priceRangeSlider = document.getElementById('priceRangeSlider');
        const totalPriceElement = document.querySelector('.total-price-amount');
        
        if (insuranceInput && priceRangeSlider && totalPriceElement) {
          insuranceInput.addEventListener('input', () => {
            this.updateTotalPriceWithInsurance();
          });
        }

        // Add event listener for price slider changes
        const priceSliders = document.querySelectorAll('input[type="range"]');
        priceSliders.forEach(slider => {
          slider.addEventListener('input', () => {
            // Clear insurance input when slider changes
            const insuranceInput = document.querySelector('#how-much-would-you-like-to-insure-your-item');
            if (insuranceInput) {
              insuranceInput.value = '';
            }
            // Update total price to remove previous insurance cost
            this.updateTotalPriceAfterSliderChange();
          });
          
          slider.addEventListener('change', () => {
            // Clear insurance input when slider changes
            const insuranceInput = document.querySelector('#how-much-would-you-like-to-insure-your-item');
            if (insuranceInput) {
              insuranceInput.value = '';
            }
            // Update total price to remove previous insurance cost
            this.updateTotalPriceAfterSliderChange();
          });
        });

      }

      updateTotalPriceWithInsurance() {
        const insuranceInput = document.querySelector('#how-much-would-you-like-to-insure-your-item');
        const priceRangeSlider = document.getElementById('priceRangeSlider');
        const totalPriceElement = document.querySelector('.total-price-amount');
        
        if (!insuranceInput || !priceRangeSlider || !totalPriceElement) return;
        
        // Get the insurance value
        const insuranceValue = parseFloat(insuranceInput.value) || 0;
        
        // Get the price range slider value (insurance cost)
        const priceRangeSliderText = priceRangeSlider.textContent;
        const insuranceCost = parseFloat(priceRangeSliderText) || 0;
        const insuranceFeeInput = document.getElementById("insuranceFeeInput");
        insuranceFeeInput.value = insuranceCost;
        
        // Get the current total price (remove currency symbol and parse)
        const currentTotalText = totalPriceElement.textContent;
        const currentTotal = parseFloat(currentTotalText.replace(/[^0-9.-]+/g, '')) || 0;
        
        // Calculate base price (current total minus any existing insurance cost)
        const basePrice = this.getBasePrice();
        
        // Calculate new total with insurance cost
        const newTotal = basePrice + insuranceCost;
        
        // Format and update the total price
        const formattedPrice = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          trailingZeroDisplay: 'stripIfInteger',
        }).format(newTotal);
        
        totalPriceElement.textContent = formattedPrice;
        
        // Trigger the price updater to recalculate everything
        if (window.priceUpdater && typeof window.priceUpdater.updateTotalPrice === 'function') {
          // Small delay to ensure insurance shipping fee is updated
          setTimeout(() => {
            window.priceUpdater.updateTotalPrice();
          }, 100);
        }
      }

      updateTotalPriceAfterSliderChange() {
        // Reset insurance fee input to 0
        const insuranceFeeInput = document.getElementById("insuranceFeeInput");
        if (insuranceFeeInput) {
          insuranceFeeInput.value = 0;
        }
        
        // Remove insurance from selectedOptions and trigger price update
        if (window.priceUpdater) {
          window.priceUpdater.selectedOptions.delete("insurance");
          window.priceUpdater.updateTotalPrice();
        }
      }

      getBasePrice() {
        // Get base price from the product price elements
        const priceElements = document.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale');
        if (priceElements.length > 0) {
          console.log(priceElements[0].textContent, "priceElements");
          const priceText = priceElements[0].textContent;
          return parseFloat(priceText.replace(/[^0-9.-]+/g, '')) || 0;
        }
        return 0;
      }

      getCartItemCount() {
        // Get cart item count from cart bubble
        const cartCountBubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (cartCountBubble) {
          const count = parseInt(cartCountBubble.textContent) || 0;
          console.log('Cart count from bubble:', count);
          return count;
        }
        
        // Alternative: check cart items directly
        const cartItems = document.querySelectorAll('.cart-item, [data-cart-item], [name="updates[]"]');
        if (cartItems.length > 0) {
          console.log('Cart count from items:', cartItems.length);
          return cartItems.length;
        }
        
        // Fallback: try to get from window.cart if available
        if (window.cart && window.cart.item_count) {
          console.log('Cart count from window.cart:', window.cart.item_count);
          return window.cart.item_count;
        }
        
        // Fallback: check for CartItems element and its currentItemCount
        const cartItemsElement = document.querySelector('cart-items');
        if (cartItemsElement && cartItemsElement.currentItemCount) {
          console.log('Cart count from cart-items element:', cartItemsElement.currentItemCount);
          return cartItemsElement.currentItemCount;
        }
        
        console.log('Cart count fallback: 0');
        return 0;
      }

      handleURLParameters() {
        // Add a small delay to ensure DOM is fully loaded
        setTimeout(() => {
          this.processURLParameters();
        }, 100);
      }

      processURLParameters() {
        // Parse URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const typeParam = urlParams.get('type');
        
        console.log('URL parameter type:', typeParam);
        
        if (!typeParam) return;
        
        // First, let's see what radio buttons actually exist
        const allMediaTypeRadios = document.querySelectorAll('input[name="properties[media-type]"]');
        console.log('Available media type radio buttons:', Array.from(allMediaTypeRadios).map(r => r.value));
        
        // Create mapping for URL parameter values to actual radio button values
        const mediaTypeMapping = {
          '8Track': ['8 Track', '8-Track', '8Track'], // Handle all variations
          'CD': ['CD'],
          'Vinyl': ['Vinyl'],
          'Cassette': ['Cassette']
        };
        
        // Find the correct media type value
        let targetMediaType = null;
        for (const [urlValue, possibleValues] of Object.entries(mediaTypeMapping)) {
          if (urlValue.toLowerCase() === typeParam.toLowerCase()) {
            // Try each possible value to find which one exists in the form
            for (const possibleValue of possibleValues) {
              const radioButton = document.querySelector(`input[name="properties[media-type]"][value="${possibleValue}"]`);
              if (radioButton) {
                targetMediaType = possibleValue;
                console.log(`Found matching radio button with value: "${possibleValue}"`);
                break;
              }
            }
            break;
          }
        }
        
        // If we didn't find a match, try direct matching with available values
        if (!targetMediaType) {
          allMediaTypeRadios.forEach(radio => {
            const radioValue = radio.value.toLowerCase().replace(/[\s-]/g, '');
            const paramValue = typeParam.toLowerCase().replace(/[\s-]/g, '');
            if (radioValue === paramValue || radioValue.includes(paramValue) || paramValue.includes(radioValue)) {
              targetMediaType = radio.value;
              console.log(`Found direct match with value: "${radio.value}"`);
            }
          });
        }
        
        // If we found a matching media type, select it
        if (targetMediaType) {
          // Uncheck all media type radio buttons first
          allMediaTypeRadios.forEach(radio => {
            radio.checked = false;
          });
          
          // Select the target media type
          const targetRadio = document.querySelector(`input[name="properties[media-type]"][value="${targetMediaType}"]`);
          if (targetRadio) {
            targetRadio.checked = true;
            
            // Trigger change event to update any related functionality
            targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
            
            console.log(`✅ Auto-selected media type: "${targetMediaType}" from URL parameter: "${typeParam}"`);
          }
        } else {
          console.log(`❌ No matching media type found for URL parameter: "${typeParam}"`);
          console.log('Available values:', Array.from(allMediaTypeRadios).map(r => `"${r.value}"`));
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
